/**
 * PIN / biometric setup.
 *
 * Three steps: choose a method, enter a PIN, confirm it. A PIN is always set
 * even when biometrics are chosen — biometrics can fail (wet hands, a mask, a
 * failed sensor) and a nurse locked out mid-visit with no fallback is a worse
 * outcome than the one this feature prevents.
 *
 * ─── Grace period ───────────────────────────────────────────────────────────
 *
 * Until the server's `requiredAt` passes, "Set up later" is offered and the
 * nurse continues into the app. After it, the skip disappears and this screen
 * is the only way forward. The deadline is server-side precisely so that
 * reinstalling the app cannot restart the clock.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
    ActivityIndicator,
    ScrollView,
    StyleSheet,
    Text,
    TouchableOpacity,
    View
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { PinPad } from "@/components/app-lock/PinPad";
import { isBiometricAvailable, PIN_LENGTH, setPin as persistPin } from "@/lib/app-lock";
import { useAppLock } from "@/lib/app-lock-context";
import { reportAppLockEnrollment } from "@/lib/session-api";
import { colors, fonts } from "@/lib/theme";

type Step = "choose" | "enter" | "confirm";

function formatDeadline(iso: string | undefined): string {
    if (!iso) {
        return "soon";
    }
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
        ? "soon"
        : d.toLocaleDateString(undefined, { month: "long", day: "numeric" });
}

export function AppLockSetup() {
    const { policy, mustEnroll, completeSetup, skipSetup } = useAppLock();

    const [step, setStep] = useState<Step>("choose");
    const [useBiometric, setUseBiometric] = useState(false);
    const [biometricAvailable, setBiometricAvailable] = useState(false);
    const [first, setFirst] = useState("");
    const [confirm, setConfirm] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        void isBiometricAvailable().then(setBiometricAvailable);
    }, []);

    const finish = useCallback(
        async (pin: string, biometric: boolean) => {
            setSaving(true);
            try {
                const method = biometric ? "biometric" : "pin";
                // Store locally FIRST. If the network call fails the device can
                // still unlock, and the server's view catches up on the next
                // enrollment attempt or session sync — the reverse order would
                // mark her enrolled on a device that cannot open.
                await persistPin(pin, method);
                await reportAppLockEnrollment(method).catch(() => {});
                completeSetup();
            } catch {
                setError("Could not save your PIN. Please try again.");
                setStep("enter");
                setFirst("");
                setConfirm("");
            } finally {
                setSaving(false);
            }
        },
        [completeSetup]
    );

    const onConfirmChange = useCallback(
        (next: string) => {
            setError(null);
            setConfirm(next);
            if (next.length < PIN_LENGTH) {
                return;
            }
            if (next !== first) {
                setError("PINs did not match. Start again.");
                setFirst("");
                setConfirm("");
                setStep("enter");
                return;
            }
            void finish(next, useBiometric);
        },
        [first, useBiometric, finish]
    );

    if (step === "choose") {
        const deadline = formatDeadline(policy?.requiredAt);
        return (
            <SafeAreaView style={styles.safe}>
                <ScrollView contentContainerStyle={styles.chooseWrap}>
                    <Text style={styles.h1}>Secure your app</Text>
                    <Text style={styles.body}>
                        Task Health shows patient information. Adding a lock keeps it private if
                        your phone is left unattended or picked up by someone else.
                    </Text>
                    <Text style={styles.body}>
                        {mustEnroll
                            ? "Setting this up is now required to continue."
                            : `You can set this up later, but it will be required from ${deadline}.`}
                    </Text>

                    {/*
                      Both choices carry the same weight on purpose. A filled
                      primary button next to an outlined one reads as a
                      recommendation, and nearly everyone takes the
                      recommendation — which would quietly funnel the whole
                      workforce onto a PIN and leave biometrics unused. Equal
                      cards with an explicit OR between them make it a real
                      choice.
                    */}
                    <TouchableOpacity
                        style={styles.option}
                        onPress={() => {
                            setUseBiometric(false);
                            setStep("enter");
                        }}
                        activeOpacity={0.8}
                        accessibilityRole="button"
                    >
                        <Text style={styles.optionTitle}>Use a PIN</Text>
                        <Text style={styles.optionHint}>Six digits you choose.</Text>
                    </TouchableOpacity>

                    <View style={styles.orRow}>
                        <View style={styles.orLine} />
                        <Text style={styles.orText}>OR</Text>
                        <View style={styles.orLine} />
                    </View>

                    {biometricAvailable ? (
                        <TouchableOpacity
                            style={styles.option}
                            onPress={() => {
                                setUseBiometric(true);
                                setStep("enter");
                            }}
                            activeOpacity={0.8}
                            accessibilityRole="button"
                        >
                            <Text style={styles.optionTitle}>Use Face ID or fingerprint</Text>
                            <Text style={styles.optionHint}>
                                Fastest. You will also set a PIN as a backup.
                            </Text>
                        </TouchableOpacity>
                    ) : (
                        // The native module is absent from this build, so the
                        // option cannot work yet. Saying so is better than
                        // hiding it — nurses ask why the option they saw a
                        // colleague use is missing. It keeps the same shape as
                        // the live option but is visibly muted, so it reads as
                        // "not yet" rather than as a button that failed.
                        <View style={[styles.option, styles.optionDisabled]}>
                            <Text style={styles.optionTitleDisabled}>
                                Use Face ID or fingerprint
                            </Text>
                            <Text style={styles.optionHint}>
                                Coming soon — available after the next app update from the App Store
                                or Google Play.
                            </Text>
                        </View>
                    )}

                    {!mustEnroll ? (
                        <TouchableOpacity onPress={skipSetup} style={styles.skip}>
                            <Text style={styles.skipText}>Set up later</Text>
                        </TouchableOpacity>
                    ) : null}
                </ScrollView>
            </SafeAreaView>
        );
    }

    return (
        <SafeAreaView style={styles.safe}>
            <PinPad
                // The re-entry is the step people miss: the screen looks almost
                // identical to the one before it, so without a step counter and
                // an explicit callout a nurse types her PIN again thinking it
                // did not register the first time.
                step={{ current: step === "enter" ? 1 : 2, total: 2 }}
                title={step === "enter" ? "Choose a PIN" : "Type it again"}
                subtitle={
                    step === "enter"
                        ? `Pick ${PIN_LENGTH} digits you will remember. Avoid your bank PIN.`
                        : undefined
                }
                notice={
                    step === "confirm" ? "Enter the same 6 digits once more to confirm" : undefined
                }
                value={step === "enter" ? first : confirm}
                length={PIN_LENGTH}
                error={error}
                disabled={saving}
                onChange={(v) => {
                    if (step === "enter") {
                        setError(null);
                        setFirst(v);
                        if (v.length === PIN_LENGTH) {
                            setStep("confirm");
                            setConfirm("");
                        }
                        return;
                    }
                    onConfirmChange(v);
                }}
                footer={saving ? <ActivityIndicator color={colors.teal400} /> : null}
            />
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    safe: { flex: 1, backgroundColor: colors.gray50 },
    chooseWrap: { padding: 24, paddingTop: 48, gap: 16 },
    h1: { fontSize: 28, fontFamily: fonts.extraBold, color: colors.gray900, marginBottom: 4 },
    body: { fontSize: 15, fontFamily: fonts.regular, color: colors.gray600, lineHeight: 22 },
    // Both options share this exact style so neither reads as the recommended
    // one. Only the disabled variant deviates, and only by muting.
    option: {
        borderRadius: 16,
        paddingVertical: 20,
        paddingHorizontal: 20,
        borderWidth: 1.5,
        borderColor: colors.teal400,
        backgroundColor: "#FFFFFF",
        minHeight: 84,
        justifyContent: "center"
    },
    optionTitle: { fontSize: 17, fontFamily: fonts.bold, color: colors.gray900 },
    optionDisabled: { backgroundColor: colors.gray100, borderColor: colors.gray200 },
    optionTitleDisabled: { fontSize: 17, fontFamily: fonts.bold, color: colors.gray500 },
    optionHint: {
        fontSize: 13,
        fontFamily: fonts.regular,
        color: colors.gray500,
        marginTop: 6,
        lineHeight: 18
    },
    orRow: { flexDirection: "row", alignItems: "center", gap: 14, marginVertical: 2 },
    orLine: { flex: 1, height: 1, backgroundColor: colors.gray300 },
    orText: {
        fontSize: 15,
        fontFamily: fonts.extraBold,
        color: colors.gray600,
        letterSpacing: 1.5
    },
    skip: { alignItems: "center", paddingVertical: 16 },
    skipText: { fontSize: 15, fontFamily: fonts.medium, color: colors.gray500 }
});
